import {
  Card,
  CardBody,
  CardProps,
  Heading,
  HStack,
  Icon,
  Text,
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { BiSolidMessageSquareError } from 'react-icons/bi';
import { IoCopyOutline } from 'react-icons/io5';
import { copyToClipboard } from 'shared/helpers/clipboard';

interface ClipboardBannerProps extends CardProps {
  content?: string;
  subContent?: string;
  LeftIcon?: IconType;
  colorScheme?: string;
  copyText?: string;
  successCopyMessage?: string;
  onCopy?(): void;
  size?: 'xs' | 'sm' | 'md';
  variant?: 'default' | 'plain';
}

const ClipboardBanner: React.FC<ClipboardBannerProps> = ({
  content,
  subContent,
  LeftIcon,
  colorScheme = 'blue',
  copyText,
  successCopyMessage,
  onCopy,
  size = 'md',
  variant = 'default',
  ...props
}) => {
  const handleCopy = () => {
    copyToClipboard(
      copyText ?? '',
      successCopyMessage ?? 'Copiado para a área de transferência',
    );
    if (onCopy) onCopy();
  };

  const sizeStyles = {
    xs: {
      cardP: '0.1rem',
      cardBodyP: '0.1rem',
      hStackSpacing: 0.5,
      iconBox: 2.5,
      headingFont: '0.8rem',
      textFont: '0.7rem',
      mt: '0.1rem',
      copyP: '0.1rem',
      copyIconBox: 2.5,
      copyTextFont: '0.7rem',
    },
    sm: {
      cardP: '0.2rem',
      cardBodyP: '0.2rem',
      hStackSpacing: 1,
      iconBox: 3,
      headingFont: '0.9rem',
      textFont: '0.75rem',
      mt: '0.2rem',
      copyP: '0.2rem',
      copyIconBox: 3,
      copyTextFont: '0.8rem',
    },
    md: {
      cardP: '1.5rem',
      cardBodyP: undefined,
      hStackSpacing: 2,
      iconBox: 6,
      headingFont: '1.2rem',
      textFont: '0.9rem',
      mt: '1rem',
      copyP: '0.5rem',
      copyIconBox: 6,
      copyTextFont: undefined,
    },
  }[size];

  const isPlain = variant === 'plain';
  const cardBg = isPlain ? 'transparent' : `${colorScheme}.400`;
  const cardColor = isPlain ? 'gray.500' : 'white';
  const headingColor = isPlain ? 'gray.500' : undefined;
  const textColor = isPlain ? 'gray.500' : undefined;

  return (
    <Card bg={cardBg} color={cardColor} p={sizeStyles.cardP} {...props}>
      <CardBody p={sizeStyles.cardBodyP}>
        <HStack spacing={sizeStyles.hStackSpacing}>
          <Icon
            as={LeftIcon ?? BiSolidMessageSquareError}
            boxSize={sizeStyles.iconBox}
            color={headingColor}
          />
          <Heading fontSize={sizeStyles.headingFont} color={headingColor}>
            {content}
          </Heading>
        </HStack>
        <Text fontSize={sizeStyles.textFont} color={textColor}>
          {subContent}
        </Text>

        <HStack
          mt={sizeStyles.mt}
          bg={isPlain ? 'gray.100' : 'blackAlpha.300'}
          rounded="0.5rem"
          p={sizeStyles.copyP}
          cursor="pointer"
          onClick={handleCopy}
          spacing={sizeStyles.hStackSpacing}
        >
          <Icon
            as={IoCopyOutline}
            boxSize={sizeStyles.copyIconBox}
            color={headingColor}
          />
          <Text w="90%" fontSize={sizeStyles.copyTextFont} color={textColor}>
            {copyText}
          </Text>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default ClipboardBanner;
